import React, { useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { IRequest as IRegisterDistDateChangedAcceptRequest } from '../hooks/useRegisterDistDateChangedAccept'
import { PageHeader, LeadSentence, Label, DisplayValue, ErrorInformation } from '../components'
import { DistDateChangedAcceptValue } from '../hooks/useSearchDistDateChangedAccept'
import { PostCdValue } from '../hooks/useSearchPostCdCity'
import { scrollTop } from '../modules/util'

interface Props {
  title: string
  data: IRegisterDistDateChangedAcceptRequest
  ddcaRow?: DistDateChangedAcceptValue
  postCdRows?: PostCdValue[]
  friendlyErrorMessage?: string
  onExecute: Function
  onCancel: Function
}

const dvStyle = { ml: 2 }

/**
 * 配達日変更受付サービス（確認）
 */
const ConfirmPage: React.FC<Props> = (props) => {
  const { title, data, ddcaRow, postCdRows, friendlyErrorMessage, onExecute, onCancel } = props
  const cityName = postCdRows?.find((row) => row.CITY_CD === data.CONSIGNEE_CITY_CD)?.CITY_NAME // 市区町村
  const pkgDroppableFlg = ddcaRow?.PKG_DROPPABLE_FLG // 置配可能フラグ（1なら受け取り方法を選択可能）
  const pkgDropName = ddcaRow?.PKG_DROP.find((row) => row.PKG_DROP_CD === data.PKG_DROP_CD)?.PKG_DROP_NAME // 置き配種類
  const desiredDate = ddcaRow?.DESIRED_DATE_LIST.find((row) => row.DESIRED_DATE === data.DIST_TARGET_DATE)?.DESIRED_DATE_FORMATTED // ご希望日
  const timezoneProvFlg = ddcaRow?.TIMEZONE_PROV_FLG // 時間帯提供フラグ（1なら希望時間帯を選択可能）
  const distTimeZoneName = ddcaRow?.DELV_TIME.find((row) => row.DIST_TIMEZONE_CD === data.DIST_TARGET_TIMEZONE_CD)?.DIST_TIMEZONE_NAME // ご希望時間帯

  // ページの先頭にスクロール
  useEffect(() => scrollTop, [])

  return (
    <Box>
      <PageHeader title={title} />

      <Grid container className='content-wrapper'>
        <Grid item xs={12} md={8} lg={6} className='content'>

          <LeadSentence>
            入力内容を確認し、よろしければ「送信する」ボタンを押して下さい。
          </LeadSentence>

          {/* 警告・エラー情報 */}
          <ErrorInformation message={friendlyErrorMessage} />

          {/* 送り状番号 */}
          <Box
            sx={{
              my: 2
            }}
          >
            <Typography component='h2'
              sx={{
                backgroundColor: '#d0e6f2',
                fontSize: '1rem',
                textAlign: 'center',
                p: 1,
              }}>
              送り状番号：
              <Typography component='span' sx={{fontSize:'1.5em', fontWeight:'bold'}}>{data.INVOICE_NO}</Typography>
            </Typography>
          </Box>

          {/* お届け先お客様情報 */}
          <Box>
            <Box
              sx={{
                my: 2,
                p: 1,
                backgroundColor: '#0002'
              }}
            >
              <Typography component='h2'>■お届け先お客様情報</Typography>
            </Box>

            <Grid container spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >

              <Grid item xs={12} md={4}><Label>お名前（漢字）：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.CONSIGNEE_NAME}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>郵便番号：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.CONSIGNEE_POSTCD}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>市区町村：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{cityName}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>町域番地：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.CONSIGNEE_ADDR}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>建物名：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.CONSIGNEE_BLDG_NAME ? data.CONSIGNEE_BLDG_NAME : '-'}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>メールアドレス：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.RESULT_NOTICE_MAIL_ADDR ? data.RESULT_NOTICE_MAIL_ADDR : '-'}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>メールアドレス（確認）：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{data.RESULT_NOTICE_MAIL_ADDR_CONFIRMATION ? data.RESULT_NOTICE_MAIL_ADDR_CONFIRMATION : '-'}</DisplayValue></Grid>

            </Grid>
          </Box>

          {/* ご希望内容 */}
          <Box>
            <Box
              sx={{
                my: 2,
                p: 1,
                backgroundColor: '#0002'
              }}
            >
              <Typography component='h2'>■ご希望内容</Typography>
            </Box>

            <Grid container spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >

              <Grid item xs={12} md={4}><Label>お受け取り方法：</Label></Grid>
              <Grid item xs={12} md={8}>
                {
                  (pkgDroppableFlg === 0) ?
                    <DisplayValue bold sx={dvStyle}>※指定不可（ご自宅）</DisplayValue>
                    :
                    <>
                      <DisplayValue bold sx={dvStyle}>
                        <>
                          {Number(data.PKG_DROP_FLG) ? '置き配' : 'ご自宅'}
                          {pkgDropName ? `（${pkgDropName}）` : ''}
                        </>
                      </DisplayValue>
                    </>
                  }
              </Grid>

              <Grid item xs={12} md={4}><Label>ご希望日：</Label></Grid>
              <Grid item xs={12} md={8}><DisplayValue bold sx={dvStyle}>{desiredDate}</DisplayValue></Grid>

              <Grid item xs={12} md={4}><Label>ご希望時間帯：</Label></Grid>
              <Grid item xs={12} md={8}>
              {
                (timezoneProvFlg === 1) ?
                  <DisplayValue bold sx={dvStyle}>{distTimeZoneName ? distTimeZoneName : '-'}</DisplayValue>
                  :
                  <DisplayValue bold sx={dvStyle}>※指定不可</DisplayValue>
              }
              </Grid>

            </Grid>
          </Box>

          {/* 送信 | キャンセル */}
          <Box>
            <Grid container
              sx={{
                mt: 4,
                mb: 1,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Grid item xs={12} md={10} lg={8}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Button
                      size='large'
                      variant='contained'
                      fullWidth
                      onClick={() => { onExecute() }}
                    >送信する</Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      size='large'
                      variant='outlined'
                      fullWidth
                      onClick={() => { onCancel() }}
                    >入力内容を修正する</Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

        </Grid>
      </Grid>

    </Box>
  )
}

export default ConfirmPage
